import { Injectable, computed, signal } from '@angular/core';
import { Subject } from 'rxjs';
import type { RemodelingRequestAsset } from '@rail/cad';

export interface SipEditorMetadata {
  sipSubmissionId: string;
  sipVersionId: string;
  espSubmissionId: string;
  espVersionId: string;
  stationId: string;
  userId?: string;
  /**
   * `generate` — a fresh SIP being produced from an ESP.
   * `view`     — an existing SIP opened read-only; the toolbar is hidden.
   * `edit`     — the same SIP opened for changes, and the only mode besides
   *              `generate` where asset-sync indexes the JSON. Without that
   *              index `buildUpdatedJson()` returns null and the save would
   *              upload `{}` over the stored SIP JSON.
   */
  mode?: 'generate' | 'view' | 'edit';
  hasUploadedSip?: boolean;
  uploadedDxfUrl?: string;
  uploadedSipJsonUrl?: string;
  digitizeStatus?: string;
  restoredFromDraft?: boolean;
}

export function checkHasUploadedSip(obj: any): boolean {
  if (!obj) return false;
  const val = obj.has_uploaded_sip ?? obj.has_sip_upload ?? obj.hasUploadedSip;
  if (val === true || val === 'true' || val === 1 || val === '1') {
    return true;
  }
  if (val === false || val === 'false' || val === 0 || val === '0') {
    return false;
  }
  if (
    obj.uploaded_dxf_file ||
    obj.uploaded_sip_json ||
    obj.dxf_file?.uploaded_download_url ||
    obj.dxf_file?.uploaded_storage_key ||
    obj.dxf_file?.uploaded_file_name
  ) {
    return true;
  }
  return false;
}

@Injectable({ providedIn: 'root' })
export class EditorStateService {
  isEditorOpen = signal(false);
  sipMetadata = signal<SipEditorMetadata | null>(null);
  hasUploadedSip = signal<boolean>(false);
  isSaved = signal(false);
  isSaving = signal(false);
  isGenerated = signal(false);
  showSaveWarning = signal(false);
  /**
   * Identifier of the ESP document open in the editor — the version id when
   * there is one, else the submission id (the same `versionId || submissionId`
   * fallback `commitEsp` uses to target digitalisation).
   *
   * Separate from `sipMetadata`, which is only populated for the SIP flow:
   * opening an ESP from Assets Validation leaves that null, so the ESP editor
   * had no identifier to name the document it was acting on. Surfaced to the
   * editor through `CAD_EDITOR_HOST.espId`.
   */
  espId = signal<string>('');
  /**
   * The document version id — what the yard-remodeling COMBINATIONS endpoint
   * keys on (it rejects anything else with
   * `Invalid remodeling input provided for document_version_id = …`).
   *
   * Deliberately separate from `espId`: that one is set when the editor opens,
   * before `loadAssetsData` has returned, so it can still be holding the raw
   * `submissionId` query param. The save endpoint accepts that; combinations
   * does not. This is populated (and re-populated) the moment the real
   * `document_version_id` is known.
   */
  documentVersionId = signal<string>('');
  /**
   * What a yard-remodeling save posts against.
   *
   * `espId` is set only when the editor opens from Assets Validation. Opening
   * the same ESP from the station's document list (View → Edit mode) sets just
   * `documentVersionId`, so Generate ESP posted with no id and failed. Both
   * name the same document version on every path that sets them.
   */
  readonly saveEspId = computed(() => this.espId() || this.documentVersionId());
  /** Station the open ESP belongs to — addresses the versions endpoint that remodeling polling watches. */
  stationId = signal<string>('');
  /**
   * Where to go when the editor closes, when the close was triggered by a
   * successful save rather than the X button.
   *
   * `'station-data'` after a yard-remodeling save: the run's progress is
   * visible on the station's document list, not on Assets Validation. `null`
   * restores the normal close destination. Consumed (and reset) by
   * `AssetsValidation.closeEditor`.
   */
  postSaveRedirect = signal<'station-data' | null>(null);
  /**
   * Bridge from `CAD_EDITOR_HOST.saveYardRemodeling` to the shell component.
   *
   * The host object is built in a provider factory, which runs in the
   * component's injector but has no reference to the instance — so it cannot
   * emit `closeRequested` or reach the component's services. The factory pushes
   * the payload here; the component subscribes and owns the call, the toast and
   * the close, exactly as the SIP save path does.
   */
  yardSaveRequested = new Subject<readonly RemodelingRequestAsset[]>();
  /**
   * The open ESP's remodelling run has completed — what offers Show Keyplan in
   * the editor. Set by station-data from the version's `status_code`; a run
   * that finishes while Assets Validation is open is covered by
   * `YardRemodelingService.state` instead. Reset by `open()`.
   */
  remodelingCompleted = signal(false);

  open(): void {
    this.isEditorOpen.set(true);
    this.isSaved.set(false);
    this.isSaving.set(false);
    this.showSaveWarning.set(false);
    this.remodelingCompleted.set(false);
  }

  close(): void {
    this.isEditorOpen.set(false);
    this.sipMetadata.set(null);
    this.espId.set('');
    this.documentVersionId.set('');
    this.stationId.set('');
    this.postSaveRedirect.set(null);
    this.isSaved.set(false);
    this.isSaving.set(false);
    this.isGenerated.set(false);
    this.showSaveWarning.set(false);
  }

  hasUnsavedChanges(): boolean {
    const meta = this.sipMetadata();
    return this.isEditorOpen() && meta?.mode === 'generate' && !this.isSaved();
  }
}
